/**
 * Alle Fotos einer Prüfung auf einen Blick.
 *
 * Aufgenommen wird im Befund, gesichtet aber am Stück: vor dem Abschluss will
 * Bernd sehen, ob jeder Mangel ein Detailfoto hat und ob nichts Unbrauchbares
 * dabei ist. Einzeln durch dreißig Befunde zu tippen wäre dafür der falsche
 * Weg.
 *
 * Fotos landen im Bericht. Ein verwackeltes oder versehentlich aufgenommenes
 * Bild gehört deshalb löschbar – aber nur, solange die Prüfung offen ist.
 * Nach dem Abschluss ist der Bericht heraus, und was darin steht, bleibt.
 */

import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { api } from '../api/klient'
import { schluessel } from '../api/abfragen'
import type { Foto } from '../api/typen'
import { Blatt } from '../bausteine/Blatt'
import { Fehlerhinweis, Ladeanzeige, Leerhinweis } from '../bausteine/Hinweise'

export function Fotogalerie({
  pruefungId,
  benennen,
  abgeschlossen,
}: {
  pruefungId: string
  /** Liefert zu einem Befund den Namen, damit unter dem Bild steht, wozu es gehört. */
  benennen: (foto: Foto) => string
  abgeschlossen: boolean
}) {
  const speicher = useQueryClient()
  const fotos = useQuery({
    queryKey: schluessel.fotos(pruefungId),
    queryFn: ({ signal }) =>
      api.holen<Foto[]>(`/api/pruefungen/${pruefungId}/fotos`, signal),
  })
  const loeschen = useMutation({
    mutationFn: (id: string) => api.loeschen(`/api/fotos/${id}`),
    onSuccess: () => {
      speicher.invalidateQueries({ queryKey: schluessel.fotos(pruefungId) })
      speicher.invalidateQueries({ queryKey: schluessel.bogen(pruefungId) })
    },
  })
  const [gross, setzeGross] = useState<Foto | null>(null)

  if (fotos.isLoading) return <Ladeanzeige text="Lädt Fotos …" />
  if (fotos.error) return <Fehlerhinweis fehler={fotos.error} />

  const liste = fotos.data ?? []

  if (liste.length === 0) {
    return (
      <Leerhinweis
        titel="Noch keine Fotos"
        text="Fotos entstehen beim Beurteilen eines Befundes."
      />
    )
  }

  return (
    <>
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 md:grid-cols-4">
        {liste.map((f) => (
          <button
            key={f.id}
            type="button"
            onClick={() => setzeGross(f)}
            className="overflow-hidden rounded-feld border-2 border-slate-200 text-left hover:border-marke-500"
          >
            <img
              src={`/api/fotos/${f.id}`}
              alt={benennen(f)}
              loading="lazy"
              className="aspect-4/3 w-full bg-slate-100 object-cover"
            />
            <div className="p-2">
              <div className="truncate text-base font-semibold">{benennen(f)}</div>
              <div className="text-sm text-slate-600">
                {f.art === 'uebersicht' ? 'Übersicht' : 'Detail'}
              </div>
            </div>
          </button>
        ))}
      </div>

      <Fehlerhinweis fehler={loeschen.error} />

      {gross && (
        <Blatt
          titel={benennen(gross)}
          untertitel={gross.art === 'uebersicht' ? 'Übersichtsfoto' : 'Detailfoto'}
          schliessen={() => setzeGross(null)}
          breit
          fuss={
            <div className="flex gap-3">
              <button
                type="button"
                className="knopf-still flex-1"
                onClick={() => setzeGross(null)}
              >
                Schließen
              </button>
              {!abgeschlossen && (
                <button
                  type="button"
                  className="knopf-warnung flex-1"
                  disabled={loeschen.isPending}
                  onClick={() => {
                    if (
                      window.confirm(
                        'Foto löschen? Es verschwindet damit auch aus dem Bericht.',
                      )
                    ) {
                      loeschen.mutate(gross.id, { onSuccess: () => setzeGross(null) })
                    }
                  }}
                >
                  {loeschen.isPending ? 'Löscht …' : 'Löschen'}
                </button>
              )}
            </div>
          }
        >
          <img
            src={`/api/fotos/${gross.id}`}
            alt={benennen(gross)}
            className="w-full rounded-feld"
          />
          {gross.breite && gross.hoehe && (
            <p className="mt-2 text-base text-slate-600">
              {gross.breite} × {gross.hoehe} Bildpunkte
              {gross.bytes ? ` · ${Math.round(gross.bytes / 1024)} KB` : ''}
            </p>
          )}
        </Blatt>
      )}
    </>
  )
}
